import React from 'react';
import clsx from 'clsx';

type Size = 'default' | 'compact' | 'text';
type ButtonProps = {
  children: React.ReactNode;
  onClick?: () => unknown;
  className?: string;
  disabled?: boolean;
  size?: Size;
  type?: 'button' | 'submit' | 'reset';
  borderRadius?: string;
};

export function Button(props: ButtonProps) {
  return (
    <BaseButton
      {...props}
      className={`bg-blue-600 font-medium text-white hover:bg-blue-700 focus:ring-sky-500 disabled:bg-gray-400 disabled:text-gray-200 ${props.className}`}
    />
  );
}

export function SecondaryButton(props: ButtonProps) {
  return (
    <BaseButton
      {...props}
      className={`border-blue-600 bg-white font-medium text-blue-600 hover:bg-blue-50 focus:ring-sky-500 disabled:text-gray-200 ${props.className}`}
    />
  );
}

export function AlertButton(props: ButtonProps) {
  return (
    <BaseButton
      {...props}
      className={`bg-red-600 font-medium text-white hover:bg-red-400 focus:ring-red-400 active:bg-red-800 disabled:bg-gray-400 disabled:text-gray-200 ${props.className}`}
    />
  );
}

export function WarningButton(props: ButtonProps) {
  return (
    <BaseButton
      {...props}
      className={`bg-yellow-700 font-semibold text-white hover:bg-yellow-800 focus:ring-yellow-500 active:bg-yellow-900 disabled:bg-gray-400 disabled:text-gray-200 ${props.className}`}
    />
  );
}

export function TertiaryButton(props: ButtonProps) {
  return (
    <BaseButton
      {...props}
      className={`border-gray-300 bg-white font-medium text-gray-900 hover:bg-gray-50 focus:ring-gray-300 disabled:text-gray-200 ${props.className}`}
    />
  );
}

type OutlineButtonProps = {
  disabled?: boolean;
  selected?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
  position?: 'left' | 'center';
};

export function OutlineButton({
  disabled = false,
  selected = false,
  onClick,
  children,
  className,
  position = 'center',
  ...rest
}: OutlineButtonProps) {
  return (
    <div className="flex items-center">
      <div className="h-[24px] rounded-full" />
      <button
        className={clsx(
          'flex h-[28px] flex-1 items-center gap-2 rounded-sm px-2 text-sm',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          getOutlineBackgroundStyle(disabled, selected),
          position === 'center' ? 'justify-center' : 'justify-start',
          className
        )}
        onClick={onClick}
        {...rest}>
        {children}
      </button>
    </div>
  );
}

function getOutlineBackgroundStyle(disabled: boolean, selected: boolean): string {
  if (disabled) return 'bg-gray-200 text-gray-400';
  if (selected) return 'border border-blue-100 bg-blue-100 text-[#3B6FEC]';
  return 'border border-[#3B6FEC] text-[#3B6FEC] hover:border-blue-700 hover:text-blue-700';
}

type BaseButtonProps = ButtonProps & { className?: string };

function getBorderRadius(borderRadius?: string): string {
  return borderRadius || 'rounded-md';
}

function BaseButton({
  children,
  disabled,
  onClick,
  className,
  size = 'default',
  type = 'button',
  borderRadius,
  ...rest
}: BaseButtonProps) {
  return (
    <button
      disabled={disabled}
      type={type}
      className={clsx(
        'focus:outline-hidden inline-flex cursor-pointer items-center border text-sm focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed',
        getBorderRadius(borderRadius),
        getSizeStyles(size),
        className
      )}
      onClick={onClick}
      {...rest}>
      {children}
    </button>
  );
}

function getSizeStyles(size: Size) {
  switch (size) {
    case 'default':
      return 'px-4 py-2';
    case 'compact':
      return 'px-2 py-1';
    case 'text':
      return 'p-0';
  }
}
